import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Select,
  notification,
  InputNumber,
  Tooltip,
  Input,
} from "antd";
import { useSelector } from "react-redux";
import APICall from "../../../../APICall";
import EndPoints from "../../../../APICall/EndPoints";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { setEditData } from "../../../../store/slice/scriptSettingsRequestSlice";

const { Option } = Select;

const ScriptSettingsForm = ({
  setShowModal,
  tab,
  editData,
  parentForm,
  brokarageDetails,
  isRent,
  watchBrokerageType,
  onFormSubmit,
  isAddingNewScript,
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const isEQUOrCRYPTO = ["NSE EQU", "CRYPTO"].includes(tab);

  // Redux state
  const scriptSettingsRequest = useSelector(
    (state) => state.scriptSettingsRequestSlice?.[tab]
  );

  // Set form values when editing
  useEffect(() => {
    if (editData) {
      form.setFieldsValue({
        scriptName: editData.scriptName,
        delivery: editData.delivery,
        intraday: editData.intraday,
        type: editData.type,
        minValue: editData.minValue,
        maxValue: editData.maxValue,
        total: editData.total,
        brokerOneIdDelivery: editData.brokerOneIdDelivery,
        brokerOneIdIntraday: editData.brokerOneIdIntraday,
        brokerTwoIdDelivery: editData.brokerTwoIdDelivery,
        brokerTwoIdIntraday: editData.brokerTwoIdIntraday,
        brokerThreeIdDelivery: editData.brokerThreeIdDelivery,
        brokerThreeIdIntraday: editData.brokerThreeIdIntraday,
      });
    }
  }, [editData, form]);

  // Validation functions
  const validateDelivery = (_, value) => {
    if (value === undefined || value === null) return Promise.resolve();
    if (value < (isRent ? 0 : brokarageDetails?.deliveryTotal) && value !== 0) {
      return Promise.reject(
        new Error(
          `Value must be greater than ${
            isRent ? "0" : brokarageDetails?.deliveryTotal
          } ${
            watchBrokerageType === "percentage" ? `and less than 1` : ""
          }`
        )
      );
    } else if (watchBrokerageType === "percentage" && value >= 1 && value !== 0) {
      return Promise.reject(
        new Error(
          `Value must be greater than ${brokarageDetails?.deliveryTotal} and less than 1`
        )
      );
    }
    return Promise.resolve();
  };

  const validateIntraday = (_, value) => {
    if (value === undefined || value === null) return Promise.resolve();
    if (value < (isRent ? 0 : brokarageDetails?.intradayTotal) && value !== 0) {
      return Promise.reject(
        new Error(
          `Value must be greater than ${
            isRent ? "0" : brokarageDetails?.intradayTotal
          } ${
            watchBrokerageType === "percentage" ? `and less than 1` : ""
          }`
        )
      );
    } else if (watchBrokerageType === "percentage" && value >= 1 && value !== 0) {
      return Promise.reject(
        new Error(
          `Value must be greater than ${brokarageDetails?.intradayTotal} and less than 1`
        )
      );
    }
    return Promise.resolve();
  };

  const validateMinValue = ({ getFieldValue }) => ({
    validator(_, value) {
      const maxValue = getFieldValue('maxValue');
      if (value === undefined || value === null) return Promise.resolve();
      if ( maxValue && value > maxValue) {
        return Promise.reject(new Error('Min value must be less than or equal to Max value'));
      }
      if (value <= 0) {
        return Promise.reject(new Error('Min value must be greater than '));
      }
      return Promise.resolve();
    }
  });

  const validateMaxValue = ({ getFieldValue }) => ({
    validator(_, value) {
      const minValue = getFieldValue('minValue');
      if (value === undefined || value === null) return Promise.resolve();
      if (minValue !== undefined && value < minValue) {
        return Promise.reject(new Error('Max value must be greater than or equal to Min value'));
      }
      if (value < 0) {
        return Promise.reject(new Error('Max value must be greater than or equal to 0'));
      }
      return Promise.resolve();
    }
  });

  const validateTotal = (_, value) => {
    if (value === undefined || value === null) return Promise.resolve();
    if (value < 0) {
      return Promise.reject(new Error('Total must be greater than or equal to 0'));
    }
    return Promise.resolve();
  };

  // Handle form submission
  const handleFinish = async (values) => {
    try {
      // Get existing record to preserve range settings (ranges field)
      const existingRecord = scriptSettingsRequest?.edited?.find(
        (item) => item.scriptName === values.scriptName
      ) || {};

      // Filter out existing data for this script
      let filtered = scriptSettingsRequest?.edited.filter(
        (item) => item.scriptName !== values.scriptName
      );

      // Add new/updated data, preserving existing ranges field
      // Script-wise settings and range settings are separate - don't overwrite ranges
      // Only include ranges if it was explicitly set in the existing record
      const updatedRecord = { ...values };
      if (existingRecord.ranges !== undefined && existingRecord.ranges !== null) {
        updatedRecord.ranges = existingRecord.ranges;
      }
      // If ranges was never set (undefined), don't include it - let it stay undefined
      
      const updatedData = [...filtered, updatedRecord];

      // Dispatch to Redux store
      dispatch(
        setEditData({
          tab: tab,
          edited: updatedData,
        })
      );

      // Call parent callback if provided (for new script addition)
      if (onFormSubmit && isAddingNewScript) {
        onFormSubmit(values);
      } else {
        // For regular edits, just close modal
        setShowModal(false);
        form.resetFields();
      }
    } catch (error) {
      console.error("Error saving script settings:", error);
      notification.error({ message: "Failed to save settings" });
    }
  };

  return (
    <Modal
      title={`Edit Script Settings - ${editData?.scriptName || "Script"}`}
      open={true}
      onCancel={() => {
        setShowModal(false);
        form.resetFields();
      }}
      footer={false}
      width={800}
    >
      <Form
        form={form}
        onFinish={handleFinish}
        layout="vertical"
        style={{ marginTop: "1rem" }}
      >
        {/* Script Name (Read-only) */}
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item
              label="Script Name"
              name="scriptName"
              rules={[{ required: true, message: "Script name is required" }]}
            >
              <Input
                style={{ width: "100%" }}
                readOnly={true}
                placeholder="Script name will be displayed here"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Original Fields */}
        <Row gutter={[16, 16]} style={{ marginTop: '2rem' }}>
          <Col span={24} style={{ textAlign: 'center', fontWeight: 'bold' }}><h4>Brokerage</h4></Col>
          <Col span={12}>
            <Form.Item
              label="Delivery"
              name="delivery"
              rules={[
                { validator: validateDelivery }
              ]}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder="Enter Delivery"
                  suffix={watchBrokerageType === "percentage" ? "%" : "Fix"}
                />
              </div>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Intraday"
              name="intraday"
              rules={[
                { validator: validateIntraday }
              ]}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder="Enter Intraday"
                  suffix={watchBrokerageType === "percentage" ? "%" : "Fix"}
                />
              </div>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Form.Item
              label="Type"
              name="type"
            >
              <Select style={{ width: "100%" }} placeholder="Select type">
                <Select.Option value="lot">Lot</Select.Option>
                <Select.Option value="quantity">Quantity</Select.Option>
                <Select.Option value="value">Value</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Min"
              name="minValue"
              rules={[
                validateMinValue
              ]}
            >
              <InputNumber style={{ width: "100%" }} min={0} placeholder="Min" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Max"
              name="maxValue"
              rules={[
                validateMaxValue
              ]}
            >
              <InputNumber style={{ width: "100%" }} min={0} placeholder="Max" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Total"
              name="total"
              rules={[
                { validator: validateTotal }
              ]}
            >
              <InputNumber style={{ width: "100%" }} min={0} placeholder="Enter Total" />
            </Form.Item>
          </Col>
        </Row>

        {/* Broker 1 Fields */}
        <Row gutter={[16, 16]}>
          <Col span={24}><h4>Broker 1 sharing</h4></Col>
          <Col span={12}>
            <Form.Item
              label="Delivery (Buy+Sell)"
              name="brokerOneIdDelivery"
            >
              <InputNumber style={{ width: "100%" }} min={0} placeholder="Enter Delivery (Buy+Sell)"  suffix={watchBrokerageType === "percentage" ? "%" : "Fix"}/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Intraday (Buy+Sell)"
              name="brokerOneIdIntraday"
            >
              <InputNumber style={{ width: "100%" }} min={0} placeholder="Enter Intraday (Buy+Sell)" suffix={watchBrokerageType === "percentage" ? "%" : "Fix"} />
            </Form.Item>
          </Col>
        </Row>
        {/* Broker 2 Fields */}
        <Row gutter={[16, 16]}>
          <Col span={24}><h4>Broker 2 sharing</h4></Col>
          <Col span={12}>
            <Form.Item
              label="Delivery (Buy+Sell)"
              name="brokerTwoIdDelivery"
            >
              <InputNumber style={{ width: "100%" }} min={0} placeholder="Enter Delivery (Buy+Sell)"  suffix={watchBrokerageType === "percentage" ? "%" : "Fix"}/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Intraday (Buy+Sell)"
              name="brokerTwoIdIntraday"
            > 
              <InputNumber style={{ width: "100%" }} min={0} placeholder="Enter Intraday (Buy+Sell)"  suffix={watchBrokerageType === "percentage" ? "%" : "Fix"}/>
            </Form.Item>
          </Col>
        </Row>
        {/* Broker 3 Fields */}
        <Row gutter={[16, 16]}>
          <Col span={24}><h4>Broker 3 sharing</h4></Col>
          <Col span={12}>
            <Form.Item
              label="Delivery (Buy+Sell)"
              name="brokerThreeIdDelivery"
            >
              <InputNumber style={{ width: "100%" }} min={0} placeholder="Enter Delivery (Buy+Sell)"  suffix={watchBrokerageType === "percentage" ? "%" : "Fix"}/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Intraday (Buy+Sell)"
              name="brokerThreeIdIntraday"
            >
              <InputNumber style={{ width: "100%" }} min={0} placeholder="Enter Intraday (Buy+Sell)"  suffix={watchBrokerageType === "percentage" ? "%" : "Fix"}/>
            </Form.Item>
          </Col>
        </Row>

        {/* Action Buttons */}
        <Row justify="center" style={{ marginTop: "2rem" }}>
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              style={{ marginRight: 8 }}
            >
              {editData ? "Update" : "Save"}
            </Button>
            <Button
              onClick={() => {
                setShowModal(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ScriptSettingsForm; 